const postal = require("./PubSub");

/**
 * Base class for everything that manages a bunch of views
 *
 * Understands the views it is responsible for and how to navigate between them
 * @abstract
 * @class
 */
const Controller = function() {
    /**
     * @type {NavigationManager} nm
     */
    let navigationManager;

    /**
     * Get the set of Views this Controller looks after.
     * The {@see RootController} registers these dynamically as the app loads
     *
     * @abstract
     * @returns {Array.<AbstractView>} view
     */
    this.getViews = function() {
        throw "Not implemented: Controller.getViews()";
    };

    /**
     * Injected by the {@see RootController} so that all Controllers can dictate navigating between views
     * @param {NavigationManager} navManager
     */
    this.setNavManager = function(navManager) {
        navigationManager = navManager;
    };

    this.getNavManager = function() {
        return navigationManager;
    };

    this.addViewToHistory = function(view) {
        navigationManager.addToHistory(view);
    };

    this.setInitialView = function(view) {
        navigationManager.setInitialView(view);
    };

    this.resetNavigation = function() {
        navigationManager.reset();
    };

    /**
     * Get the nav manager to display the passed in view
     * @param {AbstractView} view the view we want the UI to display
     * @param {boolean=} skipHistory whether or not to add an entry to the navigation history
     */
    this.showView = function(view, skipHistory) {
        let state = this.getState();

        if (state) {
            state["controllerId"] = this.getId();
        }
        navigationManager.show(view, state, skipHistory);
    };

    /**
     * Pop the current view off the {@see NavigationManager} and display the previous one
     */
    this.showPreviousView = function() {
        navigationManager.goBack();
    };

    /**
     * Returns an object containing sufficient state information for the controller such that it can be restored at a later time
     * @return {Object} The state object
     * @see {#setState}
     */
    this.getState = function() {
        // To be overridden by subclasses
        return undefined;
    };

    /**
     * Restores the state of the controller using the state object provided
     * @param {Object} state The state object containing the information needed by the controller to restore its state
     * @see {#getState}
     */
    this.setState = function(state) {
        // To be overridden by subclasses
    };

    /**
     * @abstract
     * @return {string} The ID of the controller. Must be unique across the app.
     */
    this.getId = function() {
        throw "Not implemented: Controller.getId()";
    };

    this.subscribeTo = function(topic, callback) {
        let channel = postal.channel(this.getId());
        return channel.subscribe(topic, callback);
    };

    this.subscribeToChannel = function(channel, topic, callback) {
        let bus = postal.channel(channel);
        return bus.subscribe(topic, callback);
    };

    this.publishEvent = function(channel, topic, data) {
        let bus = postal.channel(channel);
        bus.publish(topic, data || {});
    };

    this.unsubscribe = function(subscription) {
        subscription.unsubscribe();
    };
};

module.exports = Controller;